import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReusableTable from './ReusableTable';
import en from '../../copy/en';

export default function IncomeTable({ monthData, currency, onSave }) {
  const [incomeData, setIncomeData] = useState(() => {
    // Initialize with real data if available, otherwise empty default
    if (monthData?.income && monthData.income.length > 0) {
      return monthData.income;
    }
    return [{ category: '', amount: 0, id: 'default-income-row-0' }];
  });
  const isUserOperatingRef = useRef(false);
  const previousMonthDataRef = useRef(null); // Initialize to null, not monthData
  const lastSaveDataRef = useRef(null);

  // Helper function to compare data arrays
  const areDataArraysEqual = useCallback((arr1, arr2) => {
    if (!arr1 || !arr2 || arr1.length !== arr2.length) return false;
    return arr1.every((item1, index) => {
      const item2 = arr2[index];
      return item1.id === item2.id && 
             item1.category === item2.category && 
             item1.amount === item2.amount;
    });
  }, []);

  // Load data from monthData whenever it changes
  useEffect(() => {
    // Skip if user is actively editing
    if (isUserOperatingRef.current) return;

    // Skip if monthData hasn't changed
    if (monthData === previousMonthDataRef.current) return;

    const hasIncomeData = monthData?.income && monthData.income.length > 0;
    const isDifferentFromCurrent = !areDataArraysEqual(monthData?.income, incomeData);

    if (hasIncomeData && isDifferentFromCurrent) {
      // Update with real data from monthData
      setIncomeData(monthData.income);
    } else if (!hasIncomeData && incomeData.length === 0) {
      // No data and no current data - set default row
      setIncomeData([{ category: '', amount: 0, id: 'default-income-row-0' }]);
    }

    // Update the previous monthData reference
    previousMonthDataRef.current = monthData;
  }, [monthData, incomeData, areDataArraysEqual]);

  const handleDataChange = (newData) => {
    // Set flag to prevent props from overriding local state
    isUserOperatingRef.current = true;
    
    setIncomeData(newData);
    
    // Calculate total income
    const totalIncome = newData.reduce((sum, item) => sum + (item.amount || 0), 0);
    
    // Save to backend
    if (onSave) {
      onSave({ 
        income: newData,
        totalIncome: totalIncome
      });
    }
    
    // Update last save data reference
    lastSaveDataRef.current = newData;
    
    // Reset user operating flag after a short delay
    setTimeout(() => {
      isUserOperatingRef.current = false;
    }, 100);
  };

  const tooltipContent = en.finance.tooltips.income.description;

  return (
    <ReusableTable
      title="Income"
      data={incomeData}
      onDataChange={handleDataChange}
      addRowLabel="Add income source"
      currency={currency}
      showTotal={true}
      totalLabel="Total"
      tooltipContent={tooltipContent}
    />
  );
}
